import CryptoJS from "crypto-js";
import JSEncrypt from "jsencrypt";
import axios from "axios";
import { resolvePortalApiBaseUrl } from "@/constants/constants";

function getKeyApiUrl(): string {
  return `${resolvePortalApiBaseUrl()}/encrypt/get-key`;
}

export type PortalRsaPublicMaterial = { publicKey: string; keyId: string };

/**
 * Fetches RSA public key + key id. Called once per outgoing portal request (see {@code request.ts} preload interceptor).
 */
export async function getRsaPublicKey(): Promise<PortalRsaPublicMaterial> {
  const res = await axios.get(getKeyApiUrl(), {
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    params: { _: Date.now() },
  });
  return {
    publicKey: res.data.publicKey,
    keyId: res.data.keyId,
  };
}


export const encryptAES = (requestModel: object, randomKey: string) => {
  const key = CryptoJS.enc.Utf8.parse(randomKey);
  const jsonString = JSON.stringify(requestModel);
  const iv = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(jsonString),
    key,
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );
  return iv.concat(encrypted.ciphertext).toString(CryptoJS.enc.Base64);
};


export const decryptAES = (encryptedBase64: string, randomKey: string) => {
  const key = CryptoJS.enc.Utf8.parse(randomKey);
  try {
    const decodedBytes = CryptoJS.enc.Base64.parse(encryptedBase64);
    
    const iv = CryptoJS.lib.WordArray.create(decodedBytes.words.slice(0, 4));
    const ciphertext = CryptoJS.lib.WordArray.create(decodedBytes.words.slice(4));

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext } as any,
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.error("Decryption failed", e);
    return null;
  }
};

/**
 * RSA-wrapped AES session only (no body ciphertext). Use for multipart uploads so the backend can encrypt JSON responses.
 * @param preloaded from the same request's preload interceptor — avoids a second {@code get-key} HTTP call.
 */
export async function createPortalHybridSessionKeys(
  preloaded?: PortalRsaPublicMaterial
): Promise<{
  key: string;
  keyId: string;
  rawAesKey: string;
}> {
  const { publicKey, keyId } = preloaded ?? (await getRsaPublicKey());
  const randomAesKey = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);

  const rsa = new JSEncrypt();
  const formattedPubKey = publicKey.includes("BEGIN")
    ? publicKey
    : `-----BEGIN PUBLIC KEY-----\n${publicKey}\n-----END PUBLIC KEY-----`;
  rsa.setPublicKey(formattedPubKey);
  const encryptedAesKey = rsa.encrypt(randomAesKey);

  return {
    key: encryptedAesKey as string,
    keyId,
    rawAesKey: randomAesKey,
  };
}

export const encryptPayload = async (
  requestModel: object,
  preloaded?: PortalRsaPublicMaterial
) => {
  const keys = await createPortalHybridSessionKeys(preloaded);
  const encryptedData = encryptAES(requestModel, keys.rawAesKey);

  return {
    data: encryptedData,
    key: keys.key,
    keyId: keys.keyId,
    rawAesKey: keys.rawAesKey,
  };
};